/** FuryBot card components — FirmCard, DealCard, CompareCard */
import { Link } from "react-router-dom";
import { Copy, Check, ArrowRight, Flame } from "lucide-react";
import { FIRM_BRANDING } from "@/utils/formatFirmDisplay";
import { track } from "../utils/analytics";
import { DARK, LIGHT } from "../constants";

/* ── Shared CTA style tokens (matches site AffiliateCTA) ── */
const CTA_GRADIENT = "linear-gradient(135deg, #ef4444, #f59e0b 30%, #a78bfa 70%, #3b82f6)";
const CTA_BORDER = "2px";
const CTA_INNER_LIGHT = "linear-gradient(145deg, rgba(255,255,255,0.97), rgba(245,247,250,0.95))";
const CTA_INNER_DARK = "linear-gradient(145deg, rgba(240,242,248,0.95), rgba(230,235,242,0.92))";
const CTA_GLOW = "0 2px 10px rgba(239,68,68,0.08), 0 2px 10px rgba(59,130,246,0.08)";
const CTA_GLOW_HOVER = "0 4px 16px rgba(239,68,68,0.15), 0 4px 16px rgba(59,130,246,0.15)";

/** Compact gradient-border CTA matching site style */
const GradientCTA = ({ href, to, label, onClick, testId, flex = "flex-1" }) => {
  const Tag = to ? Link : "a";
  const props = to
    ? { to, onClick }
    : { href, target: "_blank", rel: "sponsored nofollow noopener noreferrer", onClick };

  return (
    <Tag
      {...props}
      className={`${flex} flex items-center justify-center group/cta`}
      style={{ padding: CTA_BORDER, background: CTA_GRADIENT, transition: "all 0.25s ease" }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = CTA_GLOW_HOVER; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = CTA_GLOW; e.currentTarget.style.transform = "translateY(0)"; }}
      data-testid={testId}
    >
      <span
        className="flex items-center justify-center gap-1.5 w-full"
        style={{
          padding: "8px 12px",
          background: CTA_INNER_LIGHT,
          color: "#0f172a",
          fontSize: "12px",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        <ArrowRight size={12} strokeWidth={2.2} style={{ opacity: 0.7 }} />
      </span>
    </Tag>
  );
};

export const CardRenderer = ({ card, onCopy, copiedCode, onNavigate }) => {
  const isDark = document.documentElement.classList.contains("dark");
  const C = isDark ? DARK : LIGHT;
  if (card.type === "firm_card") return <FirmCardChat card={card} onCopy={onCopy} copiedCode={copiedCode} C={C} onNavigate={onNavigate} />;
  if (card.type === "deal_card") return <DealCardChat card={card} onCopy={onCopy} copiedCode={copiedCode} C={C} onNavigate={onNavigate} />;
  if (card.type === "compare_card") return <CompareCardChat card={card} C={C} onNavigate={onNavigate} />;
  return null;
};

export const FirmCardChat = ({ card, onCopy, copiedCode, C, onNavigate }) => {
  const br = FIRM_BRANDING[card.slug];
  return (
    <div className="rounded-xl overflow-hidden mt-2" style={{ background: C.card, border: `1px solid ${C.border}` }}>
      <div className="px-3.5 py-3">
        <div className="flex items-center gap-2 mb-2">
          {br?.logo && <div className="firm-logo-tile w-6 h-6 rounded-lg p-0.5 flex items-center justify-center flex-shrink-0"><img src={br.logo} alt="" className="w-full h-full object-contain" /></div>}
          <span className="text-sm font-bold" style={{ color: C.text }}>{card.firm_name}</span>
          {card.fury_score && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5" style={{ background: card.fury_score >= 8 ? "#3b82f618" : "#64748b18", color: card.fury_score >= 8 ? "#3b82f6" : "#94a3b8" }} data-testid={`card-fury-${card.slug}`}><Flame size={9} />{card.fury_score}/10</span>}
          {card.discount_pct > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#22c55e18", color: "#22c55e" }}>{card.discount_pct}% OFF</span>}
        </div>
        {card.context_line && <p className="text-[11px] mb-2 px-2 py-1 rounded" style={{ background: `${C.success}10`, color: C.success, borderLeft: `2px solid ${C.success}` }}>{card.context_line}</p>}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-2.5">
          <div><span style={{ color: C.dim }}>Price:</span> <span style={{ color: C.text }}>{card.eval_price}</span></div>
          <div><span style={{ color: C.dim }}>Drawdown:</span> <span style={{ color: C.text }}>{card.drawdown}</span></div>
          <div><span style={{ color: C.dim }}>Profit Split:</span> <span style={{ color: C.text }}>{card.profit_split}</span></div>
          <div><span style={{ color: C.dim }}>Payout:</span> <span style={{ color: C.text }}>{card.payout}</span></div>
        </div>
        {card.rating && <div className="flex items-center gap-2 mb-2 text-[10px]" style={{ color: C.dim }}>{card.rating >= 4.5 ? "\u2b50" : "\u2b50"} {card.rating}/5{card.review_count ? ` \u00b7 ${card.review_count.toLocaleString()} trader mentions` : ""} \u00b7 Based on community sentiment</div>}
        {card.is_discount_live && card.discount_pct > 0 && !card.flash_deal && <div className="text-[10px] font-medium mb-2 px-2 py-1 rounded" style={{ background: "#F59E0B08", color: "#F59E0B" }}>Active deal — verified discount available</div>}
        {card.discount_code && <div className="flex items-center gap-2 mb-2"><span className="text-[10px]" style={{ color: C.dim }}>Code:</span><button onClick={() => onCopy(card.discount_code)} className="font-mono text-xs font-bold px-2 py-0.5 rounded transition-colors" style={{ background: `${C.blue}15`, color: C.blue }}>{card.discount_code} {copiedCode === card.discount_code ? <Check size={10} className="inline" /> : <Copy size={10} className="inline" />}</button></div>}
        {card.match_reason && <p className="text-xs mb-2" style={{ color: C.success }}>{card.match_reason}</p>}
        {card.flash_deal && <div className="text-xs mb-2 px-2 py-1.5 rounded-lg" style={{ background: "#F59E0B12", borderLeft: "3px solid #F59E0B" }}><span style={{ color: "#F59E0B" }}>Limited-time: {card.flash_deal.percentage} — {card.flash_deal.code || "See site"}</span></div>}
      </div>
      <div className="flex">
        <GradientCTA
          href={card.affiliate_url}
          label="Get this deal"
          onClick={() => track("cta_clicked", { firm: card.firm_name, type: "affiliate" })}
          testId={`card-cta-affiliate-${card.slug}`}
          flex="flex-[1.4]"
        />
        <GradientCTA
          to={card.profile_url}
          label="Details"
          onClick={() => { track("cta_clicked", { firm: card.firm_name, type: "profile" }); onNavigate?.(); }}
          testId={`card-cta-profile-${card.slug}`}
          flex="flex-[0.6]"
        />
      </div>
    </div>
  );
};

export const DealCardChat = ({ card, onCopy, copiedCode, C, onNavigate }) => {
  const br = FIRM_BRANDING[card.slug];
  const hasDeal = card.discountValid || card.standard_discount || card.flash_deals.length > 0;
  return (
    <div className="rounded-xl overflow-hidden mt-2" style={{ background: C.card, border: `1px solid ${C.border}` }}>
      <div className="px-3.5 py-3">
        <div className="flex items-center gap-2 mb-2.5">
          {br?.logo && <div className="firm-logo-tile w-6 h-6 rounded-lg p-0.5 flex items-center justify-center flex-shrink-0"><img src={br.logo} alt="" className="w-full h-full object-contain" /></div>}
          <span className="text-sm font-bold" style={{ color: C.text }}>{card.firm_name}</span>
          {card.discountDisplay && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#22c55e18", color: "#22c55e" }}>{card.discountDisplay}</span>}
        </div>
        {card.pricePrimary && (
          <div className="flex items-center gap-2 mb-2" data-testid={`deal-card-pricing-${card.slug}`}>
            {card.sizeLabel && <span className="text-[10px]" style={{ color: C.dim }}>{card.sizeLabel}:</span>}
            <span className="text-sm font-bold" style={{ color: card.discountValid ? "#22c55e" : C.text }}>{card.pricePrimary}</span>
            {card.priceOriginal && <span className="text-xs line-through" style={{ color: C.dim }}>{card.priceOriginal}</span>}
            {card.savingsDisplay && <span className="text-[10px]" style={{ color: "#22c55e" }}>{card.savingsDisplay}</span>}
          </div>
        )}
        {card.standard_discount && card.couponCode && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px]" style={{ color: C.dim }}>Code:</span>
            <button onClick={() => onCopy(card.couponCode)} className="font-mono text-xs font-bold px-2 py-0.5 rounded" style={{ background: `${C.blue}15`, color: C.blue }}>{card.couponCode} {copiedCode === card.couponCode ? <Check size={10} className="inline" /> : <Copy size={10} className="inline" />}</button>
            <span className="text-[10px]" style={{ color: C.dim }}>Verified</span>
          </div>
        )}
        {card.flash_deals.map((fd, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5 px-2 py-1.5 rounded-lg" style={{ background: "#F59E0B08", borderLeft: "3px solid #F59E0B" }}>
            <span className="text-xs font-bold" style={{ color: "#F59E0B" }}>⚡ {fd.percentage}</span>
            {fd.code && <button onClick={() => onCopy(fd.code)} className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${C.blue}15`, color: C.blue }}>{fd.code} {copiedCode === fd.code ? "✓" : "📋"}</button>}
            {fd.conditions && <span className="text-[10px]" style={{ color: C.dim }}>{fd.conditions.substring(0, 30)}</span>}
          </div>
        ))}
        {!hasDeal && <p className="text-xs" style={{ color: C.dim }}>No active deals right now. Check the discount page for updates.</p>}
      </div>
      <div className="flex">
        <GradientCTA
          href={card.affiliate_url}
          label="Get deal"
          testId={`deal-cta-affiliate-${card.slug}`}
          flex="flex-[1.4]"
        />
        <GradientCTA
          to={card.discount_url}
          label="Details"
          onClick={() => onNavigate?.()}
          testId={`deal-cta-details-${card.slug}`}
          flex="flex-[0.6]"
        />
      </div>
    </div>
  );
};

export const CompareCardChat = ({ card, C, onNavigate }) => {
  const { firm1, firm2, size, verdicts } = card;
  const br1 = FIRM_BRANDING[firm1.slug];
  const br2 = FIRM_BRANDING[firm2.slug];
  const Row = ({ label, v1, v2, v1Strike, v2Strike }) => (
    <div className="grid grid-cols-[90px_1fr_1fr] gap-1 py-1.5 border-b last:border-0" style={{ borderColor: C.border + "40" }}>
      <span className="text-[10px]" style={{ color: C.dim }}>{label}</span>
      <span className="text-xs font-medium" style={{ color: C.text }}>
        {v1}
        {v1Strike && <span className="ml-1 line-through text-[10px]" style={{ color: C.dim }}>{v1Strike}</span>}
      </span>
      <span className="text-xs font-medium" style={{ color: C.text }}>
        {v2}
        {v2Strike && <span className="ml-1 line-through text-[10px]" style={{ color: C.dim }}>{v2Strike}</span>}
      </span>
    </div>
  );
  return (
    <div className="rounded-xl overflow-hidden mt-2" style={{ background: C.card, border: `1px solid ${C.border}` }}>
      <div className="grid grid-cols-[90px_1fr_1fr] gap-1 px-3.5 py-2.5 border-b" style={{ borderColor: C.border }}>
        <span className="text-[10px] font-bold" style={{ color: C.dim }}>{size}</span>
        <div className="flex items-center gap-1.5">{br1?.logo && <img src={br1.logo} alt="" className="w-5 h-5 rounded object-contain bg-slate-800 p-0.5" />}<span className="text-xs font-bold" style={{ color: C.text }}>{firm1.name}</span></div>
        <div className="flex items-center gap-1.5">{br2?.logo && <img src={br2.logo} alt="" className="w-5 h-5 rounded object-contain bg-slate-800 p-0.5" />}<span className="text-xs font-bold" style={{ color: C.text }}>{firm2.name}</span></div>
      </div>
      <div className="px-3.5 py-1">
        <Row label="Price" v1={firm1.price} v2={firm2.price} v1Strike={firm1.priceOriginal} v2Strike={firm2.priceOriginal} />
        {(firm1.discount || firm2.discount) && <Row label="Discount" v1={firm1.discount || "—"} v2={firm2.discount || "—"} />}
        <Row label="Drawdown" v1={firm1.drawdown} v2={firm2.drawdown} />
        <Row label="Profit Split" v1={firm1.split} v2={firm2.split} />
        <Row label="Payout" v1={firm1.payout} v2={firm2.payout} />
        <Row label="Consistency" v1={firm1.consistency || "None"} v2={firm2.consistency || "None"} />
      </div>
      {verdicts?.length > 0 && (
        <div className="px-3.5 py-2.5 border-t" style={{ borderColor: C.border, background: `${C.blue}08` }}>
          <p className="text-[10px] font-bold uppercase mb-1" style={{ color: C.dim }}>Key Differences</p>
          {verdicts.map((v, i) => <p key={i} className="text-xs" style={{ color: C.success }}>• {v}</p>)}
        </div>
      )}
      <div className="flex">
        <GradientCTA
          href={firm1.affiliate}
          label={firm1.name.split(" ")[0]}
          testId={`compare-cta-${firm1.slug}`}
        />
        <GradientCTA
          href={firm2.affiliate}
          label={firm2.name.split(" ")[0]}
          testId={`compare-cta-${firm2.slug}`}
        />
      </div>
    </div>
  );
};
